import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  limit,
} from "firebase/firestore";
import { db } from "../firebase/firebase";

export type CheckInRecord = {
  userId: string;
  isCheckedOut: boolean;
  checkInTime: string;
  checkInEpoch?: number;
  checkInAddress: string;
  checkInPhotoUri: string;
  checkInLatitude?: number;
  checkInLongitude?: number;
  locationStatus?: string;
  workMode?: string;
  workFromHomeApproval?: "pending" | "approved" | "rejected";
  checkOutTime?: string;
  checkOutEpoch?: number;
  checkOutAddress?: string;
  checkOutPhotoUri?: string;
  checkOutLocationStatus?: string;
  duration?: string;
  taskUpdate?: string;
  createdAt?: string;
  // true when this record's checkout was performed by the auto-checkout
  // flow (checkOutAddress === "autocheckout"). Drives "absent" treatment
  // in My Check-Ins and detail-hiding in the Employee Logs check-in tab.
  markedAsAbsent?: boolean;
};

/**
 * Get YYYY-MM key from epoch ms or date string.
 * Path: organization/{orgId}/CheckInCheckOutData/{userId}/{YYYY-MM}
 */
export const getCheckInMonthKey = (date?: Date | string | number): string => {
  const d = date instanceof Date ? date : date ? new Date(date) : new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

/**
 * Get month keys needed to cover a date range.
 * Returns current month + up to 2 previous months for active check-in spanning boundaries.
 */
export const getMonthKeysForRange = (
  startDate?: Date,
  endDate?: Date
): string[] => {
  const now = new Date();
  const start = startDate || new Date(now.getFullYear(), now.getMonth() - 2, 1);
  const end = endDate || now;

  const keys = new Set<string>();
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);

  while (cursor <= endMonth) {
    keys.add(
      `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`
    );
    cursor.setMonth(cursor.getMonth() + 1);
  }

  return Array.from(keys);
};

/**
 * Build collection reference for a specific month.
 */
export const getCheckInMonthCol = (
  orgId: string,
  userId: string,
  monthKey: string
) => collection(db, `organization/${orgId}/CheckInCheckOutData/${userId}/${monthKey}`);

/**
 * Fetch all check-in records for a user across multiple months.
 * Scans current month + previous months for active check-ins spanning boundaries.
 */
export const fetchCheckInRecords = async (
  orgId: string,
  userId: string,
  monthsBack = 2
): Promise<CheckInRecord[]> => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - monthsBack, 1);
  const monthKeys = getMonthKeysForRange(start, now);

  const allRecords: CheckInRecord[] = [];

  const snapshots = await Promise.all(
    monthKeys.map((mk) => getDocs(getCheckInMonthCol(orgId, userId, mk)))
  );

  snapshots.forEach((snap) => {
    snap.docs.forEach((d) => {
      allRecords.push({ id: d.id, ...d.data() } as CheckInRecord & { id: string });
    });
  });

  return allRecords;
};

/**
 * Fetch check-in records for a user for one specific month.
 * Unlike fetchCheckInRecords (which is anchored to the current date), this
 * targets the partition of the requested month, so past months resolve correctly.
 * A record always lives in the partition of its own check-in date, so the single
 * month key is sufficient.
 */
export const fetchCheckInRecordsForMonth = async (
  orgId: string,
  userId: string,
  year: number,
  month: number // 1-12
): Promise<CheckInRecord[]> => {
  const monthKey = `${year}-${String(month).padStart(2, "0")}`;

  const snap = await getDocs(getCheckInMonthCol(orgId, userId, monthKey));

  return snap.docs.map(
    (d) => ({ id: d.id, ...d.data() } as CheckInRecord & { id: string })
  );
};

/**
 * Fetch check-in records for a user filtered by year (all months in that year).
 */
export const fetchCheckInRecordsForYear = async (
  orgId: string,
  userId: string,
  year: number
): Promise<CheckInRecord[]> => {
  const monthKeys = Array.from({ length: 12 }, (_, i) =>
    `${year}-${String(i + 1).padStart(2, "0")}`
  );

  const allRecords: CheckInRecord[] = [];

  const snapshots = await Promise.all(
    monthKeys.map((mk) => getDocs(getCheckInMonthCol(orgId, userId, mk)))
  );

  snapshots.forEach((snap) => {
    snap.docs.forEach((d) => {
      allRecords.push({ id: d.id, ...d.data() } as CheckInRecord & { id: string });
    });
  });

  return allRecords;
};

/**
 * Fetch check-in records for multiple users across multiple months.
 */
export const fetchCheckInRecordsForUsers = async (
  orgId: string,
  userIds: string[],
  monthsBack = 2
): Promise<Record<string, CheckInRecord[]>> => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - monthsBack, 1);
  const monthKeys = getMonthKeysForRange(start, now);

  const result: Record<string, CheckInRecord[]> = {};
  userIds.forEach((uid) => (result[uid] = []));

  const allPromises = userIds.flatMap((uid) =>
    monthKeys.map((mk) =>
      getDocs(getCheckInMonthCol(orgId, uid, mk)).then((snap) => ({
        uid,
        snap,
      }))
    )
  );

  const allResults = await Promise.all(allPromises);
  allResults.forEach(({ uid, snap }) => {
    snap.docs.forEach((d) => {
      result[uid].push({ id: d.id, ...d.data() } as CheckInRecord & { id: string });
    });
  });

  return result;
};

/**
 * Find the active (unchecked-out) check-in record for a user.
 */
export const findActiveCheckIn = async (
  orgId: string,
  userId: string
): Promise<(CheckInRecord & { id: string; monthKey: string }) | null> => {
  const records = await fetchCheckInRecords(orgId, userId, 2);
  const active = records.find((r) => !r.isCheckedOut) as
    | (CheckInRecord & { id: string; monthKey: string })
    | undefined;
  return active || null;
};

/**
 * Add a new check-in record.
 */
export const addCheckInRecord = async (
  orgId: string,
  userId: string,
  record: Omit<CheckInRecord, "isCheckedOut">
): Promise<{ id: string; monthKey: string }> => {
  const recordDate = record.checkInEpoch
    ? new Date(record.checkInEpoch)
    : record.checkInTime
    ? new Date(record.checkInTime)
    : new Date();
  const monthKey = getCheckInMonthKey(recordDate);
  const docRef = await addDoc(getCheckInMonthCol(orgId, userId, monthKey), {
    ...record,
    isCheckedOut: false,
  });
  return { id: docRef.id, monthKey };
};

/**
 * Update an existing check-in record (e.g., checkout).
 */
export const updateCheckInRecord = async (
  orgId: string,
  userId: string,
  monthKey: string,
  recordId: string,
  data: Partial<CheckInRecord>
): Promise<void> => {
  const docRef = doc(
    db,
    `organization/${orgId}/CheckInCheckOutData/${userId}/${monthKey}/${recordId}`
  );
  await updateDoc(docRef, data);
};

/**
 * Get month key from a check-in record's checkInEpoch or checkInTime.
 */
export const getRecordMonthKey = (record: CheckInRecord): string => {
  return getCheckInMonthKey(
    record.checkInEpoch || record.checkInTime || undefined
  );
};
